import type { Context, InputContent, Message, Role, RunAgentInput, UserMessage } from '@ag-ui/core';
import { DocumentPartSchema, FileSourceSchema } from '@ag-ui/core/schemas';
import is from '@sindresorhus/is';
import { z } from 'zod';

import { threadRunStatusSchema } from './chat-thread';
import {
    type ChatAttachmentRef,
    createJsaEntitiesMessageContentSchema,
    type FrontendContext,
    frontendContextSchema,
    regenerateMessageContentSchema,
} from './chat-user-message';

export const agUiEventName = {
    contextRefresh: 'cobrainer.context-refresh',
    textDiscarded: 'cobrainer.text-discarded',
    threadStatus: 'cobrainer.thread-status',
} as const;

export const agUiStatusActivityType = 'cobrainer.status';

/** The activity type a block of `type` streams as; its content is the block itself. */
export const agUiBlockActivityType = (type: string): string => `cobrainer.${type}`;

export const agUiStatusStepSchema = z.object({
    finished: z.boolean(),
    label: z.string(),
});

export const agUiStatusContentSchema = z.object({
    steps: z.array(agUiStatusStepSchema),
});

export type AgUiStatusContent = z.infer<typeof agUiStatusContentSchema>;

/** The request a user message stands for, as it was sent and as the server persisted it. */
export const agUiUserMessageCommandSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('new') }),
    z.object({ content: regenerateMessageContentSchema, type: z.literal('regenerate') }),
    z.object({ content: createJsaEntitiesMessageContentSchema, type: z.literal('create-jsa-entities') }),
]);

export type AgUiUserMessageCommand = z.infer<typeof agUiUserMessageCommandSchema>;

export const agUiUserMetadataSchema = z.object({
    cobrainer: z.object({
        command: agUiUserMessageCommandSchema.nullish(),
        createdAt: z.string().nullish(),
    }),
});

export type AgUiUserMetadata = z.infer<typeof agUiUserMetadataSchema>;

/** How a persisted assistant turn ended; history snapshots carry it on every message of the turn. */
export const agUiTurnStatusSchema = z.enum(['complete', 'error', 'pending']);

export type AgUiTurnStatus = z.infer<typeof agUiTurnStatusSchema>;

export const agUiAssistantMetadataSchema = z.object({
    cobrainer: z.object({
        assistantMessageId: z.string(),
        createdAt: z.string().nullish(),
        status: agUiTurnStatusSchema.nullish(),
        /** The tool a block came from, when it came from one. */
        toolId: z.string().nullish(),
    }),
});

/** The tool that produced an activity's block, or null when none did or the metadata is unreadable. */
export const readAgUiToolId = (metadata: unknown): string | null => {
    const parsed = agUiAssistantMetadataSchema.safeParse(metadata);
    return parsed.success ? (parsed.data.cobrainer.toolId ?? null) : null;
};

export const agUiThreadStatusValueSchema = z.object({
    threadStatus: threadRunStatusSchema,
});

/** The value of `cobrainer.text-discarded`: the streamed text message that led into a tool call. */
export const agUiTextDiscardedValueSchema = z.object({
    messageId: z.string(),
});

/**
 * `forwardedProps.cobrainer` of a run request: a user message command opens a turn, `continue` carries
 * on a pending turn that waits on no question (a stopped or dropped run) from its checkpoint, and
 * `retry` runs the failed latest turn again from its stored user message, which the request ends with.
 */
export const agUiCobrainerCommandSchema = z.union([
    agUiUserMessageCommandSchema,
    z.object({ type: z.literal('continue') }),
    z.object({ type: z.literal('retry') }),
]);

export type AgUiCobrainerCommand = z.infer<typeof agUiCobrainerCommandSchema>;

export type AgUiForwardedProps = {
    readonly cobrainer: AgUiCobrainerCommand;
};

const agUiForwardedPropsSchema = z.object({ cobrainer: agUiCobrainerCommandSchema });

type AgUiRunMessage = Pick<Message, 'id' | 'role'>;

const isUserMessage = <TMessage extends AgUiRunMessage>(
    message: TMessage,
): message is Extract<TMessage, { readonly role: 'user' }> => message.role === 'user';

/** The fields of a run request that decide what kind of run it is. */
export type AgUiRunShape<TMessage extends AgUiRunMessage> = Readonly<
    Partial<Pick<RunAgentInput, 'forwardedProps' | 'resume'>> & { readonly messages?: readonly TMessage[] }
>;

/** The cobrainer command a run request carries, or null when it carries none. */
export const readAgUiCommand = (forwardedProps: unknown): AgUiCobrainerCommand | null => {
    const parsed = agUiForwardedPropsSchema.safeParse(forwardedProps);
    return parsed.success ? parsed.data.cobrainer : null;
};

/**
 * The user message a run opens a turn with: its last message, when that is a user message the thread
 * does not hold yet. Every other message of the input is history the thread already holds.
 */
export const agUiNewUserMessageOf = <TMessage extends AgUiRunMessage>(
    input: AgUiRunShape<TMessage>,
    isHeld: (messageId: string) => boolean,
): Extract<TMessage, { readonly role: 'user' }> | null => {
    const last = input.messages?.at(-1);
    return is.undefined(last) || !isUserMessage(last) || isHeld(last.id) ? null : last;
};

/** A sync run only reads the thread: no new user message, no resume entries, no cobrainer command. */
export const isAgUiSyncRun = <TMessage extends AgUiRunMessage>(
    input: AgUiRunShape<TMessage>,
    isHeld: (messageId: string) => boolean,
): boolean =>
    (input.resume ?? []).length === 0 &&
    is.null(readAgUiCommand(input.forwardedProps)) &&
    is.null(agUiNewUserMessageOf(input, isHeld));

/** The roles a run shows the producer: the conversation, never the client's activity messages. */
export const agUiHistoryRoles: ReadonlySet<Role> = new Set<Role>(['assistant', 'user']);

/** The `description` of the run context entry carrying the page's {@link FrontendContext} as JSON. */
export const agUiFrontendContextDescription = 'cobrainer.frontend-context';

export const toAgUiFrontendContext = (context: FrontendContext): Context => ({
    description: agUiFrontendContextDescription,
    value: JSON.stringify(context),
});

const parseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

/** The frontend context among a run's context entries; other entries are ignored. */
export const readAgUiFrontendContext = (entries: readonly Context[]): FrontendContext | null => {
    const entry = entries.find(({ description }) => description === agUiFrontendContextDescription);
    if (is.undefined(entry)) return null;
    const parsed = frontendContextSchema.safeParse(parseJson(entry.value));
    return parsed.success ? parsed.data : null;
};

export const agUiAttachmentProvider = 'cobrainer';

/**
 * An uploaded attachment as a user message's document part: a `file` source our provider issued, whose
 * handle is the attachment id.
 */
export const agUiAttachmentPartSchema = DocumentPartSchema.extend({
    metadata: z
        .object({
            deletedAt: z.string().nullish(),
            fileName: z.string().nullish(),
        })
        .nullish(),
    source: FileSourceSchema.extend({ provider: z.literal(agUiAttachmentProvider) }),
});

/** A user message's content: plain text, or a text part followed by one document part per attachment. */
export const toAgUiUserContent = (text: string, attachments: readonly ChatAttachmentRef[]): UserMessage['content'] => {
    if (attachments.length === 0) return text;
    const parts = attachments.map(
        ({ attachmentId, fileName, mimeType }): InputContent => ({
            ...(is.nonEmptyString(fileName) ? { metadata: { fileName } } : {}),
            source: {
                ...(is.nonEmptyString(mimeType) ? { mimeType } : {}),
                provider: agUiAttachmentProvider,
                type: 'file',
                value: attachmentId,
            },
            type: 'document',
        }),
    );
    return [{ text, type: 'text' }, ...parts];
};

/** The attachments among a user message's content parts. */
export const readAgUiAttachments = (content: unknown): ChatAttachmentRef[] =>
    is.array(content)
        ? content.flatMap((part) => {
              const parsed = agUiAttachmentPartSchema.safeParse(part);
              if (!parsed.success) return [];
              const { metadata, source } = parsed.data;
              return [
                  {
                      attachmentId: source.value,
                      deletedAt: metadata?.deletedAt,
                      fileName: metadata?.fileName ?? '',
                      ...(is.undefined(source.mimeType) ? {} : { mimeType: source.mimeType }),
                  },
              ];
          })
        : [];
